import type { Entry } from '../types/raffle'
import type { Session } from '../types/session'

const ENTRIES_KEY = 'prize-wheel.entries.v1'
const HISTORY_KEY = 'prize-wheel.history.v1'
const SESSION_KEY = 'prize-wheel.session.v1'
const GUEST_ID_KEY = 'prize-wheel.guestId.v1'

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable or full — the draw still works in-memory */
  }
}

function isEntry(value: unknown): value is Entry {
  const e = value as Entry
  return (
    Boolean(e) &&
    typeof e.id === 'string' &&
    typeof e.name === 'string' &&
    typeof e.drawn === 'boolean'
  )
}

export function loadEntries(): Entry[] | null {
  const parsed = read<unknown>(ENTRIES_KEY)
  return Array.isArray(parsed) && parsed.every(isEntry) ? (parsed as Entry[]) : null
}

export function loadHistory(): Entry[] {
  const parsed = read<unknown>(HISTORY_KEY)
  return Array.isArray(parsed) && parsed.every(isEntry) ? (parsed as Entry[]) : []
}

export function saveEntries(entries: Entry[]): void {
  write(ENTRIES_KEY, entries)
}

export function saveHistory(history: Entry[]): void {
  write(HISTORY_KEY, history)
}

export function loadSession(): Session | null {
  const parsed = read<Session>(SESSION_KEY)
  if (!parsed || typeof parsed.uid !== 'string') return null
  if (parsed.roomCode !== null && typeof parsed.roomCode !== 'string') return null
  return parsed
}

export function saveSession(session: Session): void {
  write(SESSION_KEY, session)
}

/**
 * A stable per-device id for participants. They never sign in, so this is what
 * ties a person to their mute/block state across refreshes.
 */
export function loadOrCreateGuestId(): string {
  try {
    const existing = window.localStorage.getItem(GUEST_ID_KEY)
    if (existing) return existing
  } catch {
    /* ignore */
  }
  const id = `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
  try {
    window.localStorage.setItem(GUEST_ID_KEY, id)
  } catch {
    /* ignore */
  }
  return id
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY)
  } catch {
    /* ignore */
  }
}
